import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { fontSizes } from 'syf-component-library/defaultTheme';
import Inset from 'syf-component-library/ui/spacing/Inset';
import { FAR_MONEY_CHECK_ALT, FAS_EXCLAMATION_CIRCLE } from '../../../const/iconProps';
import { colors } from '../../../defaultTheme';
import { isMonthly, isPN4 } from '../../../helpers/productTypeHelpers';
import useApiMachine from '../../../hooks/machines/useApiMachine';
import { t } from '../../../strings/i18n';
import { PaymentMethodRemoveButton, PaymentMethodRemovePrompt } from '../../atoms/PaymentMethod';
import { PaymentMethodIcon } from '../../atoms/PaymentMethod/PaymentMethodName';
import { getAccountType } from './helpers';
import PaymentMethodErrorMessageBox from './PaymentMethodErrorMessageBox';
const FlexWrapper = styled.div `
  display: flex;
  flex-direction: ${(props) => { var _a; return (_a = props.direction) !== null && _a !== void 0 ? _a : 'row'; }};
  justify-content: ${(props) => { var _a; return (_a = props.justifyContent) !== null && _a !== void 0 ? _a : 'flex-start'; }};
  align-items: ${(props) => { var _a; return (_a = props.alignItems) !== null && _a !== void 0 ? _a : 'flex-start'; }};
  gap: ${(props) => (props.gap ? `${props.gap}px` : 0)};
  flex-wrap: wrap;
  height: 100%;
`;
const FlexItem = styled.div `
  flex-basis: ${(props) => { var _a; return `${(_a = props.flexBasis) !== null && _a !== void 0 ? _a : 100}%`; }};
  flex-grow: ${(props) => { var _a; return (_a = props.flexGrow) !== null && _a !== void 0 ? _a : 0; }};
  flex-shrink: ${(props) => { var _a; return (_a = props.flexShrink) !== null && _a !== void 0 ? _a : 0; }};
  max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : '100%')};
`;
const BankAccountRemoveWrapper = styled(Inset) `
  height: 100%;
`;
const DescriptionList = styled.dl `
  margin: 0;
  border-bottom: 1px solid ${colors.mediumGrey};

  .payment-method-remove-button {
    margin-right: 0;
    padding-right: 0;
    font-size: ${fontSizes.root}px;
  }
`;
// Hide the headers for mobile view
const DescriptionTerm = styled.dt `
  position: absolute;
  clip: rect(1px, 1px, 1px, 1px);
  padding: 0;
  margin: 0;
  border: 0;
  height: auto;
  width: auto;
  overflow: hidden;
`;
const DescriptionDetail = styled.dd `
  margin: 0;
`;
const AccountType = styled.p `
  font-size: ${fontSizes.small}px;
`;
const AccountNumber = styled.p `
  font-size: ${fontSizes.root}px;
`;
const BankAccountDetails = ({ name, type, paymentMedium }) => {
    const NAME = t('addPaymentMethod.name');
    const ACCOUNT_TYPE = t('addPaymentMethod.accountType');
    return (_jsx(Inset, Object.assign({ bottom: "xlarge", left: "large", top: "base" }, { children: _jsxs(FlexWrapper, Object.assign({ direction: "row", gap: 16 }, { children: [_jsx(FlexItem, Object.assign({ maxWidth: 20 }, { children: _jsx(PaymentMethodIcon, { "aria-hidden": true, icon: FAR_MONEY_CHECK_ALT, size: "1x" }) })), _jsx(FlexItem, Object.assign({ flexBasis: 0, flexGrow: 1, flexShrink: 1 }, { children: _jsxs(FlexWrapper, Object.assign({ direction: "column", gap: 7 }, { children: [_jsxs(FlexItem, { children: [_jsx(DescriptionTerm, { children: NAME }), _jsx(DescriptionDetail, Object.assign({ tabIndex: 0 }, { children: name }))] }), _jsxs(FlexItem, { children: [_jsx(DescriptionTerm, { children: ACCOUNT_TYPE }), _jsx(DescriptionDetail, Object.assign({ tabIndex: 0 }, { children: _jsx(AccountType, { children: getAccountType(type, paymentMedium) }) }))] })] })) }))] })) })));
};
const BankAccountRemove = ({ accountName, accountLast4, buttonRefs, expandedId, paymentMethodId, handleDeleteButton }) => {
    const [apiMachine] = useApiMachine();
    const { data: accountSummary } = apiMachine.context.accountSummaryQueryResult;
    const productType = accountSummary.product.type;
    const ACCOUNT_NUMBER = t('addPaymentMethod.accountNumber');
    const isMonthlyorPN4 = isMonthly(productType) || isPN4(productType);
    return (_jsx(BankAccountRemoveWrapper, Object.assign({ right: "large", top: "base" }, { children: _jsxs(FlexWrapper, Object.assign({ alignItems: "flex-end", direction: "column", justifyContent: "space-between" }, { children: [_jsx(DescriptionTerm, { children: ACCOUNT_NUMBER }), _jsx(DescriptionDetail, Object.assign({ tabIndex: 0 }, { children: _jsx(AccountNumber, { children: accountLast4 }) })), !isMonthlyorPN4 && (_jsx(PaymentMethodRemoveButton, { accountName: accountName, buttonRefs: buttonRefs, className: "payment-method-remove-button", expandedId: expandedId, paymentMethodId: paymentMethodId, onClick: () => handleDeleteButton(paymentMethodId) }))] })) })));
};
const PaymentMethodMobile = ({ buttonRefs, paymentMethod, expandedId, errorMessage, handleClosePrompt, handleDeleteButton, handleConfirmDeletePaymentMethod }) => {
    const { type, paymentMedium, bankAccountNumberLast4, cardNumberLast4, name, nickname } = paymentMethod;
    const accountLast4 = `(...${bankAccountNumberLast4 || cardNumberLast4})`;
    const accountName = nickname || name;
    return (_jsx(DescriptionList, { children: _jsxs(FlexWrapper, Object.assign({ alignItems: "stretch", direction: "row", justifyContent: "space-between" }, { children: [_jsx(FlexItem, Object.assign({ flexBasis: 0, flexGrow: 1, flexShrink: 1 }, { children: _jsx(BankAccountDetails, { name: accountName, paymentMedium: paymentMedium, type: type }) })), _jsx(FlexItem, Object.assign({ maxWidth: 135 }, { children: _jsx(BankAccountRemove, { accountLast4: accountLast4, accountName: name, buttonRefs: buttonRefs, expandedId: expandedId, handleConfirmDeletePaymentMethod: handleConfirmDeletePaymentMethod, handleDeleteButton: handleDeleteButton, paymentMethodId: paymentMethod.id }) })), expandedId === paymentMethod.id && (_jsx(FlexItem, Object.assign({ flexBasis: 100 }, { children: errorMessage ? (_jsx(Inset, Object.assign({ bottom: "base", left: "large", right: "large", top: "base" }, { children: _jsx(PaymentMethodErrorMessageBox, { errorMessage: errorMessage, handleClose: handleClosePrompt, leftIcon: FAS_EXCLAMATION_CIRCLE }) }))) : (_jsx(PaymentMethodRemovePrompt, { buttonRefs: buttonRefs, handleConfirmDeletePaymentMethod: handleConfirmDeletePaymentMethod, leftIcon: FAS_EXCLAMATION_CIRCLE, paymentMethod: paymentMethod, onClose: handleClosePrompt })) })))] })) }));
};
export default PaymentMethodMobile;
